import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions
} from '@tanstack/react-query';
import { fnApiCall } from '../config/networking';
import { useCallback, useMemo } from 'react';
import { useToast } from './useToast';

export const useInvalidateQuery = (path: string) => {
  const queryClient = useQueryClient();

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: [path] }),
    [queryClient, path]
  );

  return { invalidate };
};

interface DataWithId {
  id?: string | number;
}

// Simplified mutation options type - only include the callbacks we actually use
interface SimpleMutationOptions<TData> {
  onSuccess?: (data: TData) => void;
  onError?: (error: unknown) => void;
  skipInvalidation?: boolean;
}

const useApiMutation = <T = unknown, V = unknown>(
  method: string,
  url: string,
  id?: string | number,
  mutationOptions?: SimpleMutationOptions<T>
) => {
  const { invalidate } = useInvalidateQuery(url);
  const { addToast } = useToast();
  return useMutation<T, unknown, V>({
    mutationFn: (data: V) => {
      // data may be any shape; try to extract id when needed
      const dataWithId = data as DataWithId;
      const pathId = id ?? dataWithId?.id;
      const path = pathId ? `${url}/${pathId}` : url;
      return fnApiCall(path, addToast, { method: method, data: data });
    },
    onSuccess: async (data: T) => {
      mutationOptions?.onSuccess?.(data);
      if (!mutationOptions?.skipInvalidation) {
        await invalidate();
      }
    },
    onError: (error: unknown) => {
      mutationOptions?.onError?.(error);
    }
  });
};

interface UseApiGetOptions<T>
  extends Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'> {
  id?: string | number;
  params?: Record<string, string | number | boolean | undefined>;
}

export const useApiGet = <T = unknown>(
  url: string,
  options: UseApiGetOptions<T> = {}
) => {
  const { id, params, ...queryOptions } = options;
  const { addToast } = useToast();
  const urlParams = useMemo(
    () =>
      params ? `?${new URLSearchParams(params as Record<string, string>)}` : '',
    [params]
  );
  const path = (id ? `${url}/${id}` : url) + urlParams;
  return useQuery<T>({
    queryKey: [url, params],
    queryFn: ({ signal }) => fnApiCall(path, addToast, { signal }),
    refetchOnWindowFocus: false,
    retry: false,
    ...queryOptions
  });
};

export const useApiPost = <T = unknown, V = unknown>(
  url: string,
  mutationOptions?: SimpleMutationOptions<T>
) => {
  return useApiMutation<T, V>('post', url, undefined, mutationOptions);
};

export const useApiPut = <T = unknown, V = unknown>(
  url: string,
  id?: string | number,
  mutationOptions?: SimpleMutationOptions<T>
) => {
  return useApiMutation<T, V>('put', url, id, mutationOptions);
};

export const useApiDelete = <T = unknown, V = unknown>(
  url: string,
  id?: string,
  mutationOptions?: SimpleMutationOptions<T>
) => {
  return useApiMutation<T, V>('delete', url, id, mutationOptions);
};
